import { aircraftType } from '../data/aircraft';
import { bankLoanAnnualRate, bankLoanCapacity } from '../simulation/credit';
import { estimateRouteStats } from '../simulation/economy';
import { delistCompany } from './listing';
import type { ActiveAuction, AircraftAsset, AiManager, GameState, Route } from './types';

const clone = <T,>(value: T): T => structuredClone(value);

function nextId(state: GameState, prefix: string): string {
  const value = `${prefix}-${state.nextId}`;
  state.nextId += 1;
  return value;
}

function touch(next: GameState): GameState {
  next.updatedAt = Date.now();
  next.ledger = next.ledger.slice(-1200);
  return next;
}

export function managerNetAssets(manager: AiManager): number {
  const aircraftValue = manager.aircraft.reduce((sum, a) => sum + a.bookValue, 0);
  const debt = (manager.loans ?? []).reduce((sum, loan) => sum + loan.principalRemaining, 0);
  return Math.max(0, manager.cash + aircraftValue - debt);
}

function aiOperatingCostWeekly(manager: AiManager): number {
  const aircraftFixed = manager.aircraft.reduce((sum, a) => sum + aircraftType(a.typeId).weeklyFixedCost * a.crewSalaryModifier, 0);
  const hubCost = (manager.hubs ?? []).reduce((sum, h) => sum + h.weeklyCost, 0);
  const interest = (manager.loans ?? []).reduce((sum, loan) => sum + loan.weeklyPayment, 0);
  return aircraftFixed + hubCost + interest;
}

function asAiGameState(parent: GameState, manager: AiManager): GameState {
  return {
    ...parent,
    cash: manager.cash,
    reputation: manager.reputation,
    headquartersId: manager.headquartersId,
    hubs: manager.hubs,
    aircraft: manager.aircraft,
    routes: manager.routes,
    loans: manager.loans ?? [],
    reports: [],
    ledger: [],
    activeEffects: parent.activeEffects,
    activeMarketEvents: parent.activeMarketEvents,
    assistants: { unlockedIds: [], dismissedIds: [], hired: [] },
    fuelInventory: { gallons: 0, averageCost: 0 },
    fuelFutures: [],
    fuelStorage: [],
    aiManagers: (parent.aiManagers ?? []).filter((item) => item.id !== manager.id),
  };
}

function aiCashReserve(manager: AiManager): number {
  const weekly = aiOperatingCostWeekly(manager);
  switch (manager.strategy) {
    case 'elite':
      return Math.max(8_000_000, weekly * 8);
    case 'strategic':
      return Math.max(5_000_000, weekly * 6);
    case 'advanced':
      return Math.max(4_000_000, weekly * 5);
    default:
      return Math.max(3_000_000, weekly * 4);
  }
}

function aiAcquisitionScore(parent: GameState, bidder: AiManager, target: AiManager): number {
  const baseScore = 50;
  const netAssets = managerNetAssets(target);
  const aircraftCount = bidder.aircraft.length;

  // 规模适配：飞机越少越想买，超过 25 架兴趣大降
  let scaleScore = 0;
  if (aircraftCount <= 5) scaleScore = 25;
  else if (aircraftCount <= 12) scaleScore = 15;
  else if (aircraftCount <= 20) scaleScore = 5;
  else if (aircraftCount <= 25) scaleScore = -5;
  else scaleScore = -20;

  // 战略倾向
  const strategyBonus = bidder.strategy === 'elite' ? 12 : bidder.strategy === 'strategic' ? 8 : bidder.strategy === 'advanced' ? 4 : 0;

  // 破产包质量：目标飞机平均机况
  const avgCondition = target.aircraft.length > 0
    ? target.aircraft.reduce((sum, a) => sum + a.condition, 0) / target.aircraft.length
    : 0.5;
  const conditionScore = (avgCondition - 0.5) * 30;

  // 负债厌恶
  const debt = (target.loans ?? []).reduce((sum, loan) => sum + loan.principalRemaining, 0);
  const debtRatio = netAssets > 0 ? debt / netAssets : 0;
  const debtScore = -debtRatio * 40;

  // 网络协同：与破产公司有重叠枢纽
  const ownHubIds = new Set(bidder.hubs.map((h) => h.airportId));
  const overlapHubs = target.hubs.filter((h) => ownHubIds.has(h.airportId)).length;
  const synergyScore = overlapHubs * 5;

  // 现金压力：现金相对起价的倍数
  const startingPrice = netAssets * 0.1;
  const cashPressureScore = startingPrice > 0 ? Math.min(20, (bidder.cash / startingPrice) * 5) : 0;

  return baseScore + scaleScore + strategyBonus + conditionScore + debtScore + synergyScore + cashPressureScore;
}

function aiMaxBidAmount(parent: GameState, bidder: AiManager, target: AiManager): number {
  const netAssets = managerNetAssets(target);
  const reserve = aiCashReserve(bidder);
  const availableCash = Math.max(0, bidder.cash - reserve);

  // 可动用授信
  const aiState = asAiGameState(parent, bidder);
  const capacity = bankLoanCapacity(aiState);
  const annualRate = bankLoanAnnualRate(aiState);
  // 贷款的利息成本折算：借一年大约多付 1 年利息，按 52 周估算
  const interestCostFactor = 1 + annualRate;
  const availableCredit = capacity / interestCostFactor;

  return Math.floor(availableCash + Math.max(0, availableCredit));
}

function aiBidIncrementPercent(score: number, strategy: AiManager['strategy']): number {
  const base = strategy === 'elite' ? 3 : strategy === 'strategic' ? 2.5 : strategy === 'advanced' ? 2 : 1.5;
  const adjustment = (score - 50) / 25; // 每 25 分影响 1%
  const raw = base + adjustment;
  return Math.max(1, Math.min(5, Math.round(raw * 10) / 10));
}

function startingPriceFor(auction: ActiveAuction): number {
  return Math.max(1, Math.round(auction.baseValue * 0.1));
}

function withdrawPlayerHighBid(auction: ActiveAuction): void {
  const lastAiBid = [...auction.bids].reverse().find((bid) => bid.bidderId !== 'player');
  auction.bids = auction.bids.filter((bid) => bid.bidderId !== 'player');
  auction.currentWinnerId = lastAiBid?.bidderId ?? null;
  auction.currentPrice = lastAiBid?.amount ?? startingPriceFor(auction);
}

export function startAuction(state: GameState, targetManagerId: string, kind: ActiveAuction['kind'] = 'bankruptcy'): GameState {
  const target = state.aiManagers.find((m) => m.id === targetManagerId);
  if (!target) throw new Error('没有找到破产公司');
  if (kind === 'bankruptcy' && !target.bankrupt) throw new Error('目标公司未破产');
  if (kind === 'acquisition' && (target.bankrupt || (target.noRouteWeeks ?? 0) < 26)) throw new Error('目标公司尚未满足收购条件');

  // 进入清算或收购竞拍后立即冻结股票交易，避免拍卖期间继续交易即将消失的公司。
  const next = delistCompany(state, targetManagerId);
  const nextTarget = next.aiManagers.find((m) => m.id === targetManagerId)!;

  const baseValue = managerNetAssets(nextTarget);
  const startingPrice = Math.max(1, Math.round(baseValue * 0.1));

  next.activeAuction = {
    targetManagerId,
    kind,
    baseValue,
    currentPrice: startingPrice,
    currentWinnerId: null,
    bids: [],
    status: 'player_turn',
    round: 1,
    waitingForPlayer: true,
    minimized: false,
  };
  next.pendingAuction = true;

  return touch(next);
}

export function placeBid(
  state: GameState,
  bidderId: 'player' | string,
  incrementOrAmount: number | { amount: number },
): GameState {
  let next = clone(state);
  const auction = next.activeAuction;
  if (!auction) throw new Error('当前没有进行中的拍卖');
  if (auction.status !== 'player_turn' && bidderId === 'player') throw new Error('当前不是玩家出价回合');

  const target = next.aiManagers.find((m) => m.id === auction.targetManagerId);
  if (!target) throw new Error('拍卖目标不存在');

  const minIncrement = Math.max(1, Math.round(auction.baseValue * 0.01));
  const minPrice = auction.currentPrice + minIncrement;

  let newPrice: number;
  if (typeof incrementOrAmount === 'number') {
    const increment = Math.max(1, Math.round(auction.baseValue * (incrementOrAmount / 100)));
    newPrice = auction.currentPrice + increment;
  } else {
    newPrice = Math.max(minPrice, Math.round(incrementOrAmount.amount));
  }

  if (bidderId === 'player' && next.cash < newPrice) {
    throw new Error('现金不足以支持该出价');
  }

  if (bidderId !== 'player') {
    const bidder = next.aiManagers.find((m) => m.id === bidderId);
    if (!bidder || bidder.bankrupt) throw new Error('出价方不存在或已破产');
    if (aiMaxBidAmount(next, bidder, target) < newPrice) {
      throw new Error('AI 可动用资金不足');
    }
  }

  auction.currentPrice = newPrice;
  auction.currentWinnerId = bidderId;
  auction.bids.push({ bidderId, amount: newPrice, week: next.week });

  if (bidderId === 'player') {
    auction.status = 'ai_turn';
    auction.waitingForPlayer = false;
    next = runAiBids(next);
  }

  return touch(next);
}

export function runAiBids(state: GameState): GameState {
  const next = clone(state);
  const auction = next.activeAuction;
  if (!auction) return next;

  const target = next.aiManagers.find((m) => m.id === auction.targetManagerId);
  if (!target) {
    auction.status = 'resolved';
    next.pendingAuction = false;
    return touch(next);
  }

  const bidders = next.aiManagers.filter((m) => m.id !== auction.targetManagerId && !m.bankrupt);
  const previousWinner = auction.currentWinnerId;

  for (const bidder of bidders) {
    const score = aiAcquisitionScore(next, bidder, target);
    const maxBid = aiMaxBidAmount(next, bidder, target);
    const minNextPrice = auction.currentPrice + Math.max(1, Math.round(auction.baseValue * 0.01));

    if (score < 30 || maxBid < minNextPrice) continue;

    const increment = aiBidIncrementPercent(score, bidder.strategy);
    const bidAmount = Math.min(maxBid, auction.currentPrice + Math.max(1, Math.round(auction.baseValue * (increment / 100))));

    if (bidAmount > auction.currentPrice && bidAmount >= minNextPrice) {
      auction.currentPrice = bidAmount;
      auction.currentWinnerId = bidder.id;
      auction.bids.push({ bidderId: bidder.id, amount: bidAmount, week: next.week });
    }
  }

  auction.round += 1;

  // 如果没有任何人超过前一轮最高价，拍卖结束
  if (auction.currentWinnerId === previousWinner && previousWinner !== null) {
    return resolveAuction(next);
  }

  // 如果最高价者是玩家，继续玩家回合
  if (auction.currentWinnerId === 'player') {
    auction.status = 'player_turn';
    auction.waitingForPlayer = true;
  } else {
    auction.status = 'player_turn';
    auction.waitingForPlayer = true;
  }

  return touch(next);
}

export function abandonAuctionForPlayer(state: GameState): GameState {
  const next = clone(state);
  const auction = next.activeAuction;
  if (!auction) throw new Error('当前没有进行中的拍卖');

  if (auction.currentWinnerId === 'player') {
    withdrawPlayerHighBid(auction);
  }

  auction.status = 'ai_turn';
  auction.waitingForPlayer = false;

  let current = next;
  for (let i = 0; i < 12; i += 1) {
    const before = current.activeAuction?.currentWinnerId ?? null;
    current = runAiBids(current);
    const active = current.activeAuction;
    if (!active) return current;
    if (active.status === 'resolved') return current;
    const after = active.currentWinnerId ?? null;
    if (after === before) return resolveAuction(current);
  }
  return resolveAuction(current);
}

export function minimizeAuction(state: GameState, minimized: boolean): GameState {
  const next = clone(state);
  if (!next.activeAuction) return next;
  next.activeAuction.minimized = minimized;
  return touch(next);
}

function transferAircraftWithNewIds(
  aircraft: AircraftAsset[],
  state: GameState,
  newOwnerPrefix: string,
  loanIdMap: Map<string, string>,
): AircraftAsset[] {
  return aircraft.map((a) => ({
    ...a,
    id: nextId(state, newOwnerPrefix),
    routeIds: [],
    loanId: a.loanId ? loanIdMap.get(a.loanId) : undefined,
    // 保留 crewMood、crewSalaryModifier 等状态
  }));
}

function transferRoutesWithNewIds(routes: Route[], state: GameState, newAircraftIdMap: Map<string, string>): Route[] {
  return routes.map((route) => {
    const newAircraftIds = route.aircraftIds
      .map((id) => newAircraftIdMap.get(id))
      .filter((id): id is string => id !== undefined);
    return {
      ...route,
      id: nextId(state, 'route'),
      aircraftIds: newAircraftIds,
      stats: { ...route.stats },
    };
  });
}

export function resolveAuction(state: GameState): GameState {
  const next = clone(state);
  const auction = next.activeAuction;
  if (!auction) throw new Error('当前没有进行中的拍卖');

  const targetIndex = next.aiManagers.findIndex((m) => m.id === auction.targetManagerId);
  if (targetIndex < 0) {
    next.activeAuction = undefined;
    next.pendingAuction = false;
    return touch(next);
  }
  const target = next.aiManagers[targetIndex];

  // 若无人出价，流拍：破产公司直接清算消失
  if (auction.currentWinnerId === null) {
    next.aiManagers.splice(targetIndex, 1);
    next.activeAuction = undefined;
    next.pendingAuction = false;
    return touch(next);
  }

  const price = auction.currentPrice;

  const loanIdMap = new Map<string, string>();
  for (const loan of target.loans ?? []) {
    loanIdMap.set(loan.id, nextId(next, 'loan'));
  }

  const newAircraftIdMap = new Map<string, string>();
  const transferredAircraft = transferAircraftWithNewIds(target.aircraft, next, 'acq-aircraft', loanIdMap);
  target.aircraft.forEach((old, i) => {
    newAircraftIdMap.set(old.id, transferredAircraft[i]?.id ?? old.id);
  });
  const transferredRoutes = transferRoutesWithNewIds(target.routes, next, newAircraftIdMap);

  const transferredLoans = (target.loans ?? []).map((loan) => ({
    ...loan,
    id: loanIdMap.get(loan.id) ?? nextId(next, 'loan'),
    aircraftId: loan.aircraftId ? newAircraftIdMap.get(loan.aircraftId) ?? loan.aircraftId : undefined,
  }));

  if (auction.currentWinnerId === 'player') {
    if (next.cash < price) {
      // 资金不足，流拍
      next.aiManagers.splice(targetIndex, 1);
      next.activeAuction = undefined;
      next.pendingAuction = false;
      return touch(next);
    }
    next.cash -= price;

    next.aircraft.push(...transferredAircraft);
    next.hubs.push(...target.hubs);
    for (const route of transferredRoutes) {
      route.stats = estimateRouteStats(next, route);
    }
    next.routes.push(...transferredRoutes);
    next.loans.push(...transferredLoans);
    next.cash += target.cash;
    next.careerRecords = { ...(next.careerRecords ?? { peakFleetSize: 0, peakActiveRoutes: 0, peakHubCount: 0, totalPassengers: 0, acquiredAirlines: 0 }), acquiredAirlines: (next.careerRecords?.acquiredAirlines ?? 0) + 1 };
  } else {
    const winner = next.aiManagers.find((m) => m.id === auction.currentWinnerId);
    if (!winner) {
      next.aiManagers.splice(targetIndex, 1);
      next.activeAuction = undefined;
      next.pendingAuction = false;
      return touch(next);
    }
    winner.cash -= price;

    winner.aircraft.push(...transferredAircraft);
    winner.hubs.push(...target.hubs);
    const winnerState = asAiGameState(next, winner);
    for (const route of transferredRoutes) {
      route.stats = estimateRouteStats(winnerState, route, winner);
    }
    winner.routes.push(...transferredRoutes);
    winner.loans.push(...transferredLoans);
    winner.cash += target.cash;
  }

  // 移除已破产公司
  next.aiManagers.splice(targetIndex, 1);
  next.activeAuction = undefined;
  next.pendingAuction = false;

  return touch(next);
}
